import React, { useState } from 'react';
import styled from 'styled-components';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px;
  text-align: center;
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
  letter-spacing: -1px;
`;

const Subtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 24px);
  margin-bottom: 40px;
  opacity: 0.9;
`;

const ContentSection = styled.section`
  padding: 80px 20px;
  background: #f8f9fa;
`;

const CalculatorCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  padding: 50px;
  max-width: 900px;
  margin: 0 auto;
`;

const CalculatorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  font-size: 18px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
`;

const SliderContainer = styled.div`
  margin: 30px 0;
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e0e0e0;
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #ffd700;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #ffd700;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ResultsSection = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 16px;
  padding: 40px;
  margin-top: 40px;
  color: #333;
`;

const ResultTitle = styled.h3`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 30px;
  text-align: center;
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
`;

const ResultItem = styled.div`
  text-align: center;
`;

const ResultValue = styled.div`
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 10px;
`;

const ResultLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.8;
`;

const CalculateButton = styled.button`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
  padding: 18px 40px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

export const Calculator: React.FC = () => {
  const [values, setValues] = useState({
    currentAge: 55,
    retirementAge: 65,
    currentSavings: 250000,
    monthlyContribution: 1000,
    expectedReturn: 6,
    inflationRate: 3
  });
  
  const [results, setResults] = useState<any>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [e.target.name]: parseFloat(e.target.value) || 0
    });
  };

  const calculateRetirement = () => {
    const yearsToRetirement = values.retirementAge - values.currentAge;
    const monthsToRetirement = yearsToRetirement * 12;
    const monthlyReturn = values.expectedReturn / 100 / 12;
    const realReturn = ((1 + values.expectedReturn / 100) / (1 + values.inflationRate / 100) - 1) * 100;
    
    // Calculate future value with compound interest
    let futureValue = values.currentSavings * Math.pow(1 + values.expectedReturn / 100, yearsToRetirement);
    
    // Add monthly contributions
    if (monthlyReturn > 0) {
      futureValue += values.monthlyContribution * 
        ((Math.pow(1 + monthlyReturn, monthsToRetirement) - 1) / monthlyReturn);
    } else {
      futureValue += values.monthlyContribution * monthsToRetirement;
    }
    
    // Calculate monthly income in retirement (4% rule)
    const monthlyIncome = (futureValue * 0.04) / 12;
    
    // Inflation-adjusted value
    const inflationAdjusted = futureValue / Math.pow(1 + values.inflationRate / 100, yearsToRetirement);
    
    setResults({
      futureValue: Math.round(futureValue),
      monthlyIncome: Math.round(monthlyIncome),
      totalContributions: Math.round(values.currentSavings + (values.monthlyContribution * monthsToRetirement)),
      inflationAdjusted: Math.round(inflationAdjusted),
      realReturn: realReturn.toFixed(2)
    });
  };

  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <Title>Retirement Calculator</Title>
          <Subtitle>
            Plan your retirement with confidence using our comprehensive calculator
          </Subtitle>
        </Container>
      </HeroSection>
      
      <ContentSection>
        <Container>
          <CalculatorCard>
            <CalculatorGrid>
              <div>
                <InputGroup>
                  <Label htmlFor="currentAge">Current Age: {values.currentAge}</Label>
                  <Slider
                    type="range"
                    id="currentAge"
                    name="currentAge"
                    min="18"
                    max="80"
                    value={values.currentAge}
                    onChange={handleChange}
                  />
                </InputGroup>
                
                <InputGroup>
                  <Label htmlFor="retirementAge">Retirement Age: {values.retirementAge}</Label>
                  <Slider
                    type="range"
                    id="retirementAge"
                    name="retirementAge"
                    min="50"
                    max="85"
                    value={values.retirementAge}
                    onChange={handleChange}
                  />
                </InputGroup>
                
                <InputGroup>
                  <Label htmlFor="expectedReturn">Expected Annual Return: {values.expectedReturn}%</Label>
                  <Slider
                    type="range"
                    id="expectedReturn"
                    name="expectedReturn"
                    min="1"
                    max="12"
                    step="0.5"
                    value={values.expectedReturn}
                    onChange={handleChange}
                  />
                </InputGroup>
              </div>
              
              <div>
                <InputGroup>
                  <Label htmlFor="currentSavings">Current Savings</Label>
                  <Input
                    type="number"
                    id="currentSavings"
                    name="currentSavings"
                    value={values.currentSavings}
                    onChange={handleChange}
                    placeholder="250000"
                  />
                </InputGroup>
                
                <InputGroup>
                  <Label htmlFor="monthlyContribution">Monthly Contribution</Label>
                  <Input
                    type="number"
                    id="monthlyContribution"
                    name="monthlyContribution"
                    value={values.monthlyContribution}
                    onChange={handleChange}
                    placeholder="1000"
                  />
                </InputGroup>
                
                <InputGroup>
                  <Label htmlFor="inflationRate">Inflation Rate: {values.inflationRate}%</Label>
                  <Slider
                    type="range"
                    id="inflationRate"
                    name="inflationRate"
                    min="0"
                    max="6"
                    step="0.5"
                    value={values.inflationRate}
                    onChange={handleChange}
                  />
                </InputGroup>
              </div>
            </CalculatorGrid>
            
            <CalculateButton onClick={calculateRetirement}>
              Calculate My Retirement
            </CalculateButton>
            
            {results && (
              <ResultsSection>
                <ResultTitle>Your Retirement Projection</ResultTitle>
                <ResultGrid>
                  <ResultItem>
                    <ResultValue>${results.futureValue.toLocaleString()}</ResultValue>
                    <ResultLabel>Total at Retirement</ResultLabel>
                  </ResultItem>
                  <ResultItem>
                    <ResultValue>${results.monthlyIncome.toLocaleString()}</ResultValue>
                    <ResultLabel>Monthly Income</ResultLabel>
                  </ResultItem>
                  <ResultItem>
                    <ResultValue>${results.inflationAdjusted.toLocaleString()}</ResultValue>
                    <ResultLabel>Today's Dollars</ResultLabel>
                  </ResultItem>
                  <ResultItem>
                    <ResultValue>{results.realReturn}%</ResultValue>
                    <ResultLabel>Real Return</ResultLabel>
                  </ResultItem>
                </ResultGrid>
              </ResultsSection>
            )}
          </CalculatorCard>
        </Container>
      </ContentSection>
    </SharedLayout>
  );
};